import {
  MatchStateFilter,
  PlatformLinkingProvider,
  PlatformType,
  Role,
  TournamentStateFilter,
} from '@okletsplay/core';
import { createSelector } from '@reduxjs/toolkit';
import { StoreName } from 'app/enums/store';
import { AppState } from 'app/interfaces/store';
import isEmpty from 'lodash/isEmpty';
import {
  LolPlatformLinkingProviderEntity,
  PlatformLinkingProviderEntity,
} from 'modules/platform-linking/interfaces';
import { useMemo } from 'react';
import keycloak from 'keycloak';

import { SessionState } from './interfaces/session-state';

const selectSessionState = (state: AppState): SessionState => state[StoreName.Session];

const rolesSelector = createSelector(
  selectSessionState,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  (session: SessionState) => session.entity.user?.roles as Role[]
);

const activeUserSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.user
);

const isAuthorizedSelector = createSelector(
  selectSessionState,
  (session: SessionState) => !isEmpty(session.entity.user) && keycloak.authenticated
);

const connectionsSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.user?.connections ?? []
);

const pathnameSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.redirect?.pathname
);

const searchSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.redirect?.search
);

const lolPlatformLinkingProviderSelector = createSelector(
  selectSessionState,
  (session: SessionState) =>
    session.entity.user?.connections?.find(
      (platform) => platform?.platformId === PlatformLinkingProvider.LeagueOfLegends
    ) as LolPlatformLinkingProviderEntity
);

const isChatPinnedSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.isChatPinned
);

const isLeaderboardExpandedSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.isLeaderboardExpanded ?? false
);

const isCreateTournamentFeatureAllowedSelector = createSelector(
  selectSessionState,
  (session: SessionState) => {
    if (!keycloak?.authenticated) {
      return true;
    }

    if (!session.entity.user.roles) {
      return false;
    }

    return session.entity.user.roles?.includes(Role.TournamentCreator);
  }
);

const isTournamentAdminSelector = createSelector(selectSessionState, (session: SessionState) => {
  if (!keycloak?.authenticated) {
    return false;
  }

  if (!session.entity.user.roles) {
    return false;
  }

  return session.entity.user.roles?.includes(Role.TournamentAdministrator);
});

const tournamentFilterSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.tournamentFilter ?? TournamentStateFilter.All
);

const matchFilterSelector = createSelector(
  selectSessionState,
  (session: SessionState) => session.entity.matchFilter ?? MatchStateFilter.All
);

const selectConnectionFactory = (platform?: PlatformLinkingProvider | PlatformType) =>
  createSelector(
    selectSessionState,
    (session: SessionState): PlatformLinkingProviderEntity | null => {
      if (!platform) {
        return null;
      }

      const result = session.entity?.user?.connections?.find(
        (connection) => connection?.platformId === platform
      );

      if (!result) {
        return null;
      }

      return result;
    }
  );

export const sessionSelector = {
  roles: rolesSelector,
  activeUser: activeUserSelector,
  isAuthorized: isAuthorizedSelector,
  connections: connectionsSelector,
  isTournamentAdmin: isTournamentAdminSelector,
  pathname: pathnameSelector,
  search: searchSelector,
  lolPlatformLinkingProvider: lolPlatformLinkingProviderSelector,
  isCreateTournamentFeatureAllowed: isCreateTournamentFeatureAllowedSelector,
  isChatPinned: isChatPinnedSelector,
  isLeaderboardExpanded: isLeaderboardExpandedSelector,
  tournamentFilter: tournamentFilterSelector,
  matchFilter: matchFilterSelector,
};

export const sessionSelectorFactory = {
  useConnection: (platform?: PlatformLinkingProvider | PlatformType) =>
    useMemo(() => selectConnectionFactory(platform), [platform]),
};
